import { useMutation } from "convex/react";
import { useCallback, useEffect, useRef } from "react";

import type { ErrorInfo } from "@/components/Notifications/ErrorInfoDisplay";
import { getVapidPublicKey } from "@/components/Notifications/getVapidPublicKey";
import { Button } from "@/components/ui/button";
import { stringifyError } from "@/helpers/stringifyError";
import { api } from "@convex/_generated/api";

interface SubscribeButtonProps {
  pushManager: PushManager;
  setCurrentSubscription: (subscription: PushSubscription | null) => void;
  setErrorInfo: (info: ErrorInfo) => void;
}

export const SubscribeButton = ({
  pushManager,
  setCurrentSubscription,
  setErrorInfo,
}: SubscribeButtonProps) => {
  const savePushSubscription = useMutation(api.notificationSubscriptions.save);

  const requestPermissions = useCallback(async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        console.log("Notification permission granted");

        // Check if we're already subscribed.
        let subscription = await pushManager.getSubscription(); // TODO: could we call this even if permissions are not granted? What happens if we do? Maybe we could simplify the code a bit.

        // If not, create a new subscription.
        if (!subscription) {
          try {
            subscription = await pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: getVapidPublicKey(),
            });
            console.log("New subscription created:", subscription);
          } catch (error) {
            setErrorInfo({
              message:
                "Something went wrong while we were subscribing to push notifications. Please try again.",
              details: stringifyError(error),
            });
            console.error("Error subscribing to push notifications:", error);
            return;
          }
        }

        // Save the subscription.
        try {
          await savePushSubscription({
            pushSubscription: JSON.stringify(subscription),
            isDeclarativeWebPush: "pushManager" in window,
            userAgent: navigator.userAgent,
            preferredNotificationTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            preferredNotificationHour: 8, // Default to sending notifications at 8 AM.
          });
        } catch (error) {
          setErrorInfo({
            message:
              "Something went wrong saving the push subscription to the server. Please try again.",
            details: stringifyError(error),
          });
          console.error("Error saving push subscription:", error);
          return;
        }

        // Wait until it's saved before updating local state so we don't see a flash of "Loading..." in the UI.
        setCurrentSubscription(subscription);
        console.log("Push subscription saved");
      } else {
        setErrorInfo({
          message:
            "We need your permission to send notifications - check your settings and try again.",
        });
        console.log("Notification permission denied or dismissed");
      }
    } catch (error) {
      setErrorInfo({
        message:
          "Something went wrong with requesting permissions to send notifications. Please try again.",
        details: stringifyError(error),
      });
      console.error("Error requesting notification permission:", error);
    }
  }, [savePushSubscription, pushManager, setCurrentSubscription, setErrorInfo]);

  return <SubscribeButtonInternal onClick={requestPermissions} />;
};

function SubscribeButtonInternal({ onClick }: { onClick: () => void }) {
  // iOS is picky about ensuring that the call to request permissions
  // is made directly in the click handler of the button, and react abstractions get in the way of this.
  // So we attach the click handler directly with a ref.
  const requestPermissionButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const button = requestPermissionButtonRef.current;
    if (button) {
      button.addEventListener("click", onClick);
    }
    return () => {
      if (button) {
        button.removeEventListener("click", onClick);
      }
    };
  }, [onClick, requestPermissionButtonRef]);

  return <Button ref={requestPermissionButtonRef}>Enable notifications</Button>;
}
